import {
  Button,
  Checkbox,
  Drawer,
  Empty,
  List,
  message,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import plantTypeService from '../../services/plant-type.service';

const { Title, Text } = Typography;

function ManageCapabilities({ open, onClose, plantType }) {
  const [loading, setLoading] = useState(false);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [allCapabilities, setAllCapabilities] = useState([]);
  const [assignedCapabilities, setAssignedCapabilities] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && plantType) {
      fetchAllCapabilities();
      fetchAssignedCapabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plantType]);

  const fetchAllCapabilities = async () => {
    setCapabilitiesLoading(true);
    try {
      const response = await plantTypeService.getPlantCapabilityList(
        1,
        1000,
        '',
        '',
      );
      if (response.data) {
        const capabilities = response.data.results?.data || [];
        const capabilitiesArray = Array.isArray(capabilities) ? capabilities : [];
        setAllCapabilities(capabilitiesArray);
      }
    } catch (error) {
      message.error('Failed to fetch capabilities');
      console.error('Error fetching capabilities:', error);
    } finally {
      setCapabilitiesLoading(false);
    }
  };

  const fetchAssignedCapabilities = async () => {
    setLoading(true);
    try {
      const response = await plantTypeService.getPlantTypeCapabilities(
        plantType.id,
        1,
        1000,
      );
      if (response.data) {
        const assigned = response.data.results?.data || [];
        const assignedArray = Array.isArray(assigned) ? assigned : [];
        setAssignedCapabilities(assignedArray);
        setSelectedCapabilities(
          assignedArray.map(cap => {
            // Extract ID: prioritize capability.id if capability is an object, otherwise use direct ID fields
            if (cap.capability && typeof cap.capability === 'object' && cap.capability.id) {
              return cap.capability.id;
            }
            if (cap.capability_id) {
              return cap.capability_id;
            }
            if (cap.capability && (typeof cap.capability === 'string' || typeof cap.capability === 'number')) {
              return cap.capability;
            }
            return cap.id;
          }),
        );
      }
    } catch (error) {
      message.error('Failed to fetch assigned capabilities');
      console.error('Error fetching assigned capabilities:', error);
      setAssignedCapabilities([]);
      setSelectedCapabilities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCapabilityToggle = capabilityId => {
    setSelectedCapabilities(prev => {
      if (prev.includes(capabilityId)) {
        return prev.filter(id => id !== capabilityId);
      } else {
        return [...prev, capabilityId];
      }
    });
  };

  const handleSave = async () => {
    if (!plantType || plantType.status !== 'Active') {
      message.warning('Cannot assign capabilities to inactive plant types');
      return;
    }

    setSaving(true);
    try {
      const safeAssignedCapabilities = Array.isArray(assignedCapabilities) 
        ? assignedCapabilities 
        : [];
      const currentAssignedIds = safeAssignedCapabilities.map(cap => {
        // Extract ID: prioritize capability.id if capability is an object, otherwise use direct ID fields
        if (cap.capability && typeof cap.capability === 'object' && cap.capability.id) {
          return cap.capability.id;
        }
        if (cap.capability_id) {
          return cap.capability_id;
        }
        if (cap.capability && (typeof cap.capability === 'string' || typeof cap.capability === 'number')) {
          return cap.capability;
        }
        return cap.id;
      });
      const toAdd = selectedCapabilities.filter(
        id => !currentAssignedIds.includes(id),
      );
      const toRemove = currentAssignedIds.filter(
        id => !selectedCapabilities.includes(id),
      );

      const promises = [];

      for (const capabilityId of toAdd) {
        promises.push(
          plantTypeService.assignPlantTypeCapability({
            plant_type: plantType.id,
            capability: capabilityId,
          }),
        );
      }

      for (const assignment of safeAssignedCapabilities) {
        // Extract ID: prioritize capability.id if capability is an object, otherwise use direct ID fields
        let assignmentCapabilityId;
        if (assignment.capability && typeof assignment.capability === 'object' && assignment.capability.id) {
          assignmentCapabilityId = assignment.capability.id;
        } else if (assignment.capability_id) {
          assignmentCapabilityId = assignment.capability_id;
        } else if (assignment.capability && (typeof assignment.capability === 'string' || typeof assignment.capability === 'number')) {
          assignmentCapabilityId = assignment.capability;
        } else {
          assignmentCapabilityId = assignment.id;
        }
        if (toRemove.includes(assignmentCapabilityId)) {
          const assignmentId =
            assignment.id || assignment.plant_type_capability_id;
          if (assignmentId) {
            promises.push(
              plantTypeService.removePlantTypeCapability(assignmentId),
            );
          }
        }
      }

      await Promise.all(promises);
      message.success('Capabilities updated successfully');
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update capabilities';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const isCapabilityAssigned = capabilityId => {
    return selectedCapabilities.includes(capabilityId);
  };

  const getAssignedCapabilityInfo = capabilityId => {
    if (!Array.isArray(assignedCapabilities)) {
      return null;
    }
    return assignedCapabilities.find(cap => {
      // Extract ID: prioritize capability.id if capability is an object, otherwise use direct ID fields
      let capId;
      if (cap.capability && typeof cap.capability === 'object' && cap.capability.id) {
        capId = cap.capability.id;
      } else if (cap.capability_id) {
        capId = cap.capability_id;
      } else if (cap.capability && (typeof cap.capability === 'string' || typeof cap.capability === 'number')) {
        capId = cap.capability;
      } else {
        capId = cap.id;
      }
      return capId === capabilityId;
    });
  };

  const isPlantTypeActive = plantType?.status === 'Active';

  return (
    <Drawer
      title={`Manage Capabilities - ${plantType?.name || ''}`}
      width={600}
      onClose={onClose}
      open={open}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!isPlantTypeActive}
            >
              Save Changes
            </Button>
          </Space>
        </div>
      }
    >
      {!isPlantTypeActive && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px',
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '4px',
          }}
        >
          <Text type="warning">
            <CloseCircleOutlined /> This plant type is inactive. Capability
            assignment is disabled.
          </Text>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <Title level={5}>Available Capabilities</Title>
        <Text type="secondary">
          Select capabilities to assign to this plant type
        </Text>
      </div>

      {loading || capabilitiesLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : allCapabilities.length === 0 ? (
        <Empty description="No capabilities available" />
      ) : (
        <List
          dataSource={allCapabilities}
          renderItem={capability => {
            const capabilityId = capability.id;
            const isAssigned = isCapabilityAssigned(capabilityId);
            const assignedInfo = getAssignedCapabilityInfo(capabilityId);

            return (
              <List.Item
                style={{
                  padding: '12px',
                  border: '1px solid #f0f0f0',
                  marginBottom: '8px',
                  borderRadius: '4px',
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Checkbox
                        checked={isAssigned}
                        onChange={() => handleCapabilityToggle(capabilityId)}
                        disabled={!isPlantTypeActive}
                      />
                      <Text strong>{capability.name || capability.code}</Text>
                      {isAssigned && (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          Assigned
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <div>
                      {capability.description && (
                        <Text type="secondary">{capability.description}</Text>
                      )}
                      {capability.code && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Code: {capability.code}
                          </Text>
                        </div>
                      )}
                      {assignedInfo && assignedInfo.created_at && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Assigned on:{' '}
                            {new Date(assignedInfo.created_at).toLocaleDateString()}
                          </Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}

      <div style={{ marginTop: '24px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
        <Text type="secondary">
          <strong>Selected:</strong> {selectedCapabilities.length} of{' '}
          {allCapabilities.length} capabilities
        </Text>
      </div>
    </Drawer>
  );
}

export default ManageCapabilities;

